import React, { useState } from "react";
import { Loader2, Calendar, DollarSign, Users, Building2, Upload, X } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StudioOfferForm({ onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    submission_type: "studio",
    studio_name: "",
    studio_contact_person: "",
    studio_email: "",
    studio_website: "",
    twitter_handle: "",
    instagram_handle: "",
    studio_logo_url: "",
    shoot_dates: "",
    budget_range: "",
    scene_concept: "",
    models_needed: 1,
    flights_needed: false,
    accommodation_needed: false,
    additional_requirements: ""
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : (type === "number" ? parseInt(value) || 1 : value)
    }));
  };


  const handleLogoUpload = async (file) => {
    if (!file) return;
    if (!file.type || !file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Logo too large (max 10MB)");
      return;
    }
    
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name || "studio_logo.jpg");
      const res = await axios.post(`${API}/model-applications/upload-logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = res.data?.secure_url;
      if (!url) throw new Error("No URL returned");
      setFormData(prev => ({ ...prev, studio_logo_url: url }));
      toast.success("Studio logo uploaded");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Upload failed";
      toast.error(`Upload failed: ${msg}`);
    } finally {
      setUploading(false);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log("Studio form submit triggered", formData);
    
    if (!formData.studio_name || !formData.studio_email) {
      toast.error("Please fill in studio name and email");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        name: formData.studio_name,
        email: formData.studio_email
      };
      console.log("Sending studio submission:", payload);
      
      const response = await api.post("/api/submissions/", payload);
      console.log("Studio submission response:", response);
      
      toast.success("Studio shooting offer submitted!");
      if (typeof onSuccess === "function") {
        onSuccess();
      }
    } catch (error) {
      console.error("Studio submission error:", error);
      const errorMsg = error?.response?.data?.detail || error?.message || "Failed to submit";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-white mb-2">Studio Shooting Offer</h2>
        <p className="text-gray-400">Propose a collaboration shoot</p>
      </div>

      {/* Studio Info */}
      <div className="bg-black/40 backdrop-blur-xl rounded-2xl p-6 border border-white/10 space-y-4">
        <h3 className="text-xl font-bold text-white flex items-center gap-2">
          <Building2 className="w-5 h-5 text-purple-400" />
          Studio Information
        </h3>
        
        <div>
          <label className="block text-gray-300 mb-2 font-medium">Studio Name <span className="text-pink-400">*</span></label>
          <input type="text" name="studio_name" value={formData.studio_name} onChange={handleChange} required
            className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition"
            placeholder="Your studio name" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-300 mb-2 font-medium">Contact Person</label>
            <input type="text" name="studio_contact_person" value={formData.studio_contact_person} onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition"
              placeholder="Your name" />
          </div>
          <div>
            <label className="block text-gray-300 mb-2 font-medium">Email <span className="text-pink-400">*</span></label>
            <input type="email" name="studio_email" value={formData.studio_email} onChange={handleChange} required
              className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition"
              placeholder="studio@example.com" />
          </div>
        </div>

        <div>
          <label className="block text-gray-300 mb-2 font-medium">Studio Website</label>
          <input type="url" name="studio_website" value={formData.studio_website} onChange={handleChange}
            className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition"
            placeholder="https://yourstudio.com" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-300 mb-2 font-medium">X (Twitter) Handle</label>
            <input type="text" name="twitter_handle" value={formData.twitter_handle} onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition"
              placeholder="@yourstudio" />
          </div>
          <div>
            <label className="block text-gray-300 mb-2 font-medium">Instagram Handle</label>
            <input type="text" name="instagram_handle" value={formData.instagram_handle} onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition"
              placeholder="@yourstudio" />
          </div>
        </div>

        {/* Studio Logo Upload */}
        <div>
          <label className="block text-gray-300 mb-2 font-medium">Studio Logo</label>
          {formData.studio_logo_url ? (
            <div className="relative inline-block">
              <img src={formData.studio_logo_url} alt="Studio logo" className="w-32 h-32 object-cover rounded-xl border-2 border-purple-500" />
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, studio_logo_url: "" }))}
                className="absolute -top-2 -right-2 p-1 bg-red-500 rounded-full hover:bg-red-600 transition"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
          ) : (
            <div>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => e.target.files?.[0] && handleLogoUpload(e.target.files[0])}
                className="hidden"
                id="studio-logo-upload"
                disabled={uploading}
              />
              <label
                htmlFor="studio-logo-upload"
                className={`inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-medium cursor-pointer transition ${
                  uploading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5" />
                    Upload Logo
                  </>
                )}
              </label>
            </div>
          )}
        </div>
      </div>

      {/* Shoot Details */}
      <div className="bg-black/40 backdrop-blur-xl rounded-2xl p-6 border border-white/10 space-y-4">
        <h3 className="text-xl font-bold text-white flex items-center gap-2">
          <Calendar className="w-5 h-5 text-purple-400" />
          Shoot Details
        </h3>

        <div>
          <label className="block text-gray-300 mb-2 font-medium">Proposed Shoot Dates</label>
          <input type="text" name="shoot_dates" value={formData.shoot_dates} onChange={handleChange}
            className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition"
            placeholder="e.g., Oct 15-17, 2026 or flexible" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-300 mb-2 font-medium">Budget Range</label>
            <input type="text" name="budget_range" value={formData.budget_range} onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition"
              placeholder="€500-1000 or negotiable" />
          </div>
          <div>
            <label className="block text-gray-300 mb-2 font-medium">Models Needed</label>
            <input type="number" name="models_needed" value={formData.models_needed} onChange={handleChange} min="1"
              className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition" />
          </div>
        </div>

        <div>
          <label className="block text-gray-300 mb-2 font-medium">Scene Concept</label>
          <textarea name="scene_concept" value={formData.scene_concept} onChange={handleChange} rows={4}
            className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition resize-none"
            placeholder="Describe your shoot concept..." />
        </div>

        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-gray-300 cursor-pointer">
            <input type="checkbox" name="flights_needed" checked={formData.flights_needed} onChange={handleChange}
              className="w-5 h-5 rounded border-gray-600 text-purple-600 focus:ring-purple-500" />
            <span>Flights Needed</span>
          </label>
          <label className="flex items-center gap-2 text-gray-300 cursor-pointer">
            <input type="checkbox" name="accommodation_needed" checked={formData.accommodation_needed} onChange={handleChange}
              className="w-5 h-5 rounded border-gray-600 text-purple-600 focus:ring-purple-500" />
            <span>Accommodation Needed</span>
          </label>
        </div>

        <div>
          <label className="block text-gray-300 mb-2 font-medium">Additional Requirements</label>
          <textarea name="additional_requirements" value={formData.additional_requirements} onChange={handleChange} rows={3}
            className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition resize-none"
            placeholder="Any other details..." />
        </div>
      </div>

      <button type="submit" disabled={loading}
        className="w-full py-4 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-600 text-white font-bold text-lg hover:from-purple-600 hover:to-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-purple-500/50">
        {loading ? (<><Loader2 className="w-5 h-5 animate-spin" />Submitting...</>) : "Submit Studio Offer"}
      </button>
    </form>
  );
}